import React, { useState, useEffect } from "react";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore"; // Importar getDoc
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./FormEmpresa.css";

const FormEmpresa = () => {
  const db = getFirestore();
  const auth = getAuth();
  const storage = getStorage();

  const [formData, setFormData] = useState({
    companyName: "",
    logo: null,
    position: "",
    employees: "",
    industry: "",
    industryDetail: "",
    city: "",
    address: "",
    contactEmail: "",
    contactPhone: "",
    website: "",
  });

  const [logoError, setLogoError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");


  // Obtener datos guardados de Firestore (si existen)
  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const empresaDocRef = doc(db, "empresas", user.uid);
          const docSnap = await getDoc(empresaDocRef); // Cambiar a getDoc
          if (docSnap.exists()) {
            const data = docSnap.data();
            setFormData({
              ...data,
              logo: data.logo || null, // Asegurar que el logo sea manejado correctamente
            });
          }
        } catch (error) {
          console.error("Error al obtener los datos de la empresa:", error);
        }
      }
    };
    fetchData();
  }, [auth, db]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validación de tipo de archivo (solo imágenes)
      const fileType = file.type.split("/")[0];
      if (fileType !== "image") {
        setLogoError("Solo se permiten archivos de imagen.");
        setFormData({ ...formData, logo: null });
        return;
      }

      // Validación de tamaño (máximo 4MB)
      if (file.size > 4 * 1024 * 1024) {
        setLogoError("El archivo debe ser menor de 4MB.");
        setFormData({ ...formData, logo: null });
        return;
      }

      setLogoError("");
      setFormData({ ...formData, logo: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const user = auth.currentUser;

    if (!user) {
      alert("Por favor, inicia sesión para continuar.");
      return;
    }

    setLoading(true);
    try {
      let logoURL = formData.logo;

      // Subir el logo si se ha seleccionado un archivo
      if (formData.logo && typeof formData.logo !== "string") {
        const logoRef = ref(storage, `logo-empresa/${user.uid}`);
        await uploadBytes(logoRef, formData.logo);
        logoURL = await getDownloadURL(logoRef);
      }

      const empresaDoc = doc(db, "empresas", user.uid);
      await setDoc(empresaDoc, {
        ...formData,
        logo: logoURL, // Usar la URL del logo
        userId: user.uid,
        updatedAt: new Date(),
      });

      setSuccessMessage("Datos actualizados con éxito.");
      setErrorMessage("");
    } catch (error) {
      console.error("Error al guardar la información de la empresa: ", error);
      setErrorMessage("Ocurrió un problema. Intenta de nuevo.");
      setSuccessMessage(""); 
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="form-empresa" onSubmit={handleSubmit}>
      <div className="form-empresa__group">
        <label className="form-empresa__label">Nombre de la empresa</label>
        <input
          type="text"
          name="companyName"
          value={formData.companyName || ""}
          onChange={handleChange}
          className="form-empresa__input"
          required
        />
      </div>

      <div className="form-empresa__group">
        <label className="form-empresa__label">Logo de la empresa</label>
        <input
          type="file"
          name="logo"
          onChange={handleFileChange}
          className="form-empresa__input"
          accept="image/*"
        />
        {logoError && <p className="error-message">{logoError}</p>}
      </div>

      <div className="form-empresa__group">
        <label className="form-empresa__label">Puesto que ocupas</label>
        <input
          type="text"
          name="position"
          value={formData.position || ""} 
          onChange={handleChange}
          className="form-empresa__input"
        />
      </div>

      <div className="form-empresa__group">
        <label className="form-empresa__label">Cantidad de empleados</label>
        <input
          type="number"
          name="employees"
          value={formData.employees || ""}
          onChange={handleChange}
          className="form-empresa__input"
        />
      </div>

      <div className="form-empresa__group">
        <label className="form-empresa__label">Rubro de la empresa</label>
        <input
          type="text"
          name="industry"
          value={formData.industry || ""}
          onChange={handleChange}
          className="form-empresa__input"
          required
        />
      </div>

      <div className="form-empresa__group">
        <label className="form-empresa__label">¿A que se dedica la empresa</label>
        <input
          type="text"
          name="industryDetail"
          value={formData.industryDetail || ""}
          onChange={handleChange}
          className="form-empresa__input"
          required
        />
      </div>

      <div className="form-empresa__group">
        <label className="form-empresa__label">Ciudad</label>
        <input
          type="text"
          name="city"
          value={formData.city || ""}
          onChange={handleChange}
          className="form-empresa__input"
        />
      </div>

      <div className="form-empresa__group">
        <label className="form-empresa__label">Dirección</label>
        <input
          type="text"
          name="address"
          value={formData.address || ""}
          onChange={handleChange}
          className="form-empresa__input"
        />
      </div>

      <div className="form-empresa__group">
        <label className="form-empresa__label">Correo de contacto</label>
        <input
          type="email"
          name="contactEmail"
          value={formData.contactEmail || ""}
          onChange={handleChange}
          className="form-empresa__input"
          required
        />
      </div>

      <div className="form-empresa__group">
        <label className="form-empresa__label">Teléfono de contacto</label>
        <input
          type="tel"
          name="contactPhone"
          value={formData.contactPhone || ""}
          onChange={handleChange}
          className="form-empresa__input"
        />
      </div>

      <div className="form-empresa__group">
        <label className="form-empresa__label">Página web o redes sociales</label>
        <input
          type="text"
          name="website"
          value={formData.website || ""}
          onChange={handleChange}
          className="form-empresa__input"
        />
      </div>

      <button type="submit" className="btn btn-primary" disabled={loading}>
        {loading ? "Guardando..." : "Guardar información"}
      </button>

      {successMessage && <div className="success-message">{successMessage}</div>}
      {errorMessage && <div className="error-message">{errorMessage}</div>}

    </form>
  );
};

export default FormEmpresa;
